import React, { useState, useContext, useEffect, useCallback } from 'react'
import { Box, Stack, Link, Divider } from '@mui/material'
import { grayText, defaultText } from '@/constant'
import { CheckCircleFilled, Exclamation } from '@/icon'
import Text from '@/components/Text'
import { AuthContext } from '@/layout/Auth'
import {
  BasicCard,
  UserIcon,
  UserName,
  UserId,
  UserInfo,
  UserInfoLabel,
  GrayText,
  LabelIcon,
  IdIcon,
  BindStatus,
} from './components'
import { isEmpty } from '@/utils'
import BindEmail from './bindEmail'
import BindMobile from './bindMobile'
import BindWechat from './bindWechat'
import Verification from './verification'
import { getUserInfo } from '@/api'
import { useQuery } from '@tanstack/react-query'
import EditableName from './editableName'
import { useSearchParams } from 'react-router-dom'
import { Message } from '@cx/ui'

export type BindType = 'email' | 'mobile' | 'wechat'

export const Basic: React.FC<unknown> = () => {
  const {
    authInfo: { userInfo },
    updateAuthInfo,
  } = useContext(AuthContext)
  const [bindEmail, setBindEmail] = useState(false)
  const [bindMobile, setBindMobile] = useState(false)
  const [bindWechat, setBindWechat] = useState(false)
  const [searchParams] = useSearchParams()

  const queryUserInfo = useQuery(['getUserInfo'], getUserInfo, {
    onSuccess: (result) => {
      updateAuthInfo({
        auth: true,
        userInfo: result,
        verified: result?.is_certified === 1,
      })
    },
    onError: () => {
      updateAuthInfo({
        auth: false,
        userInfo: null,
        verified: false,
        orgInfo: null,
      })
    },
    enabled: false,
  })

  const handleBind = useCallback(
    (type: BindType) => () => {
      switch (type) {
        case 'email': {
          setBindEmail(true)
          break
        }
        case 'mobile': {
          setBindMobile(true)
          break
        }
        case 'wechat': {
          setBindWechat(true)
          break
        }
      }
    },
    [],
  )

  const onSave = useCallback(() => {
    queryUserInfo.refetch()
  }, [queryUserInfo])

  const onCloseBind = useCallback(
    (type: BindType) => () => {
      switch (type) {
        case 'email': {
          setBindEmail(false)
          break
        }
        case 'mobile': {
          setBindMobile(false)
          break
        }
        case 'wechat': {
          setBindWechat(false)
          break
        }
      }
      queryUserInfo.refetch()
    },
    [queryUserInfo],
  )

  useEffect(() => {
    const errorMsg = searchParams.get('errorMsg')
    if (errorMsg) {
      Message.error(errorMsg)
    }
  }, [searchParams])

  return (
    <Box>
      <Stack sx={{ gap: '60px' }}>
        <BasicCard>
          <UserIcon src={userInfo?.head_img_url} variant='square' />
          <UserName>
            {/*   @ts-ignore */}
            <EditableName value={userInfo?.nickname} onSave={onSave} />
            <UserId>
              <IdIcon />
              <Text sx={{ fontSize: '16px' }} weight='thin'>
                {userInfo?.id}
              </Text>
            </UserId>
          </UserName>
          <UserInfo>
            <UserInfoLabel>
              <LabelIcon type={'mail'} />
              邮箱
            </UserInfoLabel>
            {isEmpty(userInfo?.mail) ? <GrayText>暂无</GrayText> : userInfo?.mail}
            <InfoBindStatus binded={!isEmpty(userInfo?.mail)} onClickBind={handleBind('email')} />
          </UserInfo>
          {/* <UserInfo>
            <UserInfoLabel>
              <LabelIcon type={'mobile'}/>
              手机
            </UserInfoLabel>
            {isEmpty(userInfo?.phone_number) ? (
              <GrayText>暂无</GrayText>
            ) : (
              userInfo?.phone_number
            )}
            <InfoBindStatus
              binded={!isEmpty(userInfo?.phone_number)}
              onClickBind={() => handleBind("mobile")}
            />
          </UserInfo> */}
          <UserInfo>
            <UserInfoLabel>
              <LabelIcon type={'wechat'} />
              微信
            </UserInfoLabel>
            {isEmpty(userInfo?.wechat_nickname) ? <GrayText>暂无</GrayText> : userInfo?.wechat_nickname}
            <InfoBindStatus binded={!isEmpty(userInfo?.wechat_nickname)} onClickBind={handleBind('wechat')} />
          </UserInfo>
          <Divider />
          <Text size='xs' weight='thin'>
            注册时间：{userInfo?.created_at}
          </Text>
        </BasicCard>
        <Verification />
      </Stack>
      <BindEmail open={bindEmail} onClose={onCloseBind('email')} />
      <BindMobile open={bindMobile} onClose={onCloseBind('mobile')} />
      <BindWechat open={bindWechat} onClose={onCloseBind('wechat')} />
    </Box>
  )
}

const InfoBindStatus = ({
  binded,
  allowChange = false,
  onClickBind,
}: {
  binded: boolean
  allowChange?: boolean
  onClickBind?: () => void
}) => {
  return (
    <BindStatus binded={binded?.toString()}>
      {binded ? (
        <>
          <CheckCircleFilled /> 已绑定
          {allowChange && (
            <>
              <div style={{ color: grayText }}>|</div>
              <Link sx={{ color: defaultText }} underline='none' onClick={onClickBind}>
                修改
              </Link>
            </>
          )}
        </>
      ) : (
        <>
          <Exclamation /> 未绑定
          <div style={{ color: grayText }}>|</div>
          <Link sx={{ color: defaultText }} underline='none' onClick={onClickBind}>
            绑定
          </Link>
        </>
      )}
    </BindStatus>
  )
}

export default Basic
